import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  staffService,
  CreateStaffData,
  UpdateStaffData,
} from '../services/staffService';

const STAFF_QUERY_KEY = ['staff'];

export function useStaffList(filters?: {
  role?: string;
  active?: boolean;
  department?: string;
}) {
  return useQuery({
    queryKey: [...STAFF_QUERY_KEY, filters],
    queryFn: () => staffService.listStaff(filters),
  });
}

export function useCreateStaff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStaffData) => staffService.createStaff(data),
    onSuccess: (staff) => {
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
      toast.success(`${staff.name} has been added as ${staff.role}`);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to create staff member';
      toast.error(message);
    },
  });
}

export function useUpdateStaff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      staffId,
      data,
    }: {
      staffId: string;
      data: UpdateStaffData;
    }) => staffService.updateStaff(staffId, data),
    onSuccess: (staff) => {
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
      toast.success(`${staff.name} has been updated`);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to update staff member';
      toast.error(message);
    },
  });
}

export function useDeleteStaff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (staffId: string) => staffService.deleteStaff(staffId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
      toast.success('Staff member has been removed');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to delete staff member';
      toast.error(message);
    },
  });
}

export function useActivateStaff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (staffId: string) => staffService.activateStaff(staffId),
    onSuccess: (staff) => {
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
      toast.success(`${staff.name} has been activated`);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to activate staff member';
      toast.error(message);
    },
  });
}

export function useDeactivateStaff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (staffId: string) => staffService.deactivateStaff(staffId),
    onSuccess: (staff) => {
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
      toast.success(`${staff.name} has been deactivated`);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Failed to deactivate staff member';
      toast.error(message);
    },
  });
}
